import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';
import {
  ProductSupplierQuote,
  CreateSupplierQuotePayload,
  UpdateSupplierQuotePayload,
  ApiResponse,
  PagedSupplierQuotesResponse,
  GetProductSupplierQuotesParams
} from '../model/productSupplierQuote';

@Injectable({
  providedIn: 'root'
})
export class ProductSupplierQuoteService {
  protected basePath = '/';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  /**
   * Get supplier quotes of a product (supports pagination, variant filter & Sieve search: supplier_name@=, quote_time>=yyyy-MM-dd)
   * GET /api/products/{productId}/supplierQuotes?page=1&pageSize=10&filters=...&sorts=...&productTypeId={guid}
   */
  getProductSupplierQuotes(
    productId: string,
    paramsOrProductTypeId?: GetProductSupplierQuotesParams | string
  ): Observable<ApiResponse<PagedSupplierQuotesResponse>> {
    let httpParams = new HttpParams();

    let options: GetProductSupplierQuotesParams = {};
    if (typeof paramsOrProductTypeId === 'string') {
      options = { productTypeId: paramsOrProductTypeId };
    } else if (paramsOrProductTypeId) {
      options = { ...paramsOrProductTypeId };
    }

    if (options.page !== undefined && options.page !== null) {
      httpParams = httpParams.set('page', String(options.page));
    }
    if (options.pageSize !== undefined && options.pageSize !== null) {
      httpParams = httpParams.set('pageSize', String(options.pageSize));
    }
    if (options.sorts) {
      httpParams = httpParams.set('sorts', options.sorts);
    }
    if (options.productTypeId) {
      httpParams = httpParams.set('productTypeId', options.productTypeId);
    }

    // Build Sieve filters
    const filterParts: string[] = [];
    if (options.filters && options.filters.trim()) {
      filterParts.push(options.filters.trim());
    }

    const supplierName = options.supplier_name || (options as any).supplierName;
    if (supplierName && supplierName.trim()) {
      filterParts.push(`supplier_name@=${supplierName.trim()}`);
    }

    const quoteTime = options.quote_time || (options as any).quoteTime;
    if (quoteTime) {
      let dateStr = '';
      if (quoteTime instanceof Date) {
        const year = quoteTime.getFullYear();
        const month = String(quoteTime.getMonth() + 1).padStart(2, '0');
        const day = String(quoteTime.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
      } else if (typeof quoteTime === 'string' && quoteTime.trim()) {
        dateStr = quoteTime.trim().split('T')[0];
      }
      if (dateStr) {
        filterParts.push(`quote_time>=${dateStr}`);
      }
    }

    if (filterParts.length > 0) {
      httpParams = httpParams.set('filters', filterParts.join(','));
    }

    return this.httpClient.get<ApiResponse<PagedSupplierQuotesResponse>>(
      `${this.basePath}/api/products/${productId}/supplierQuotes`,
      {
        params: httpParams,
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders
      }
    );
  }

  /**
   * Get details of a single supplier quote by ID
   * GET /api/productSupplierQuotes/{id}
   */
  getSupplierQuoteById(id: string): Observable<ApiResponse<ProductSupplierQuote>> {
    return this.httpClient.get<ApiResponse<ProductSupplierQuote>>(
      `${this.basePath}/api/productSupplierQuotes/${id}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders
      }
    );
  }

  /**
   * Create a new supplier quote for a product / variant
   * POST /api/productSupplierQuotes
   */
  createSupplierQuote(
    payload: CreateSupplierQuotePayload
  ): Observable<ApiResponse<ProductSupplierQuote>> {
    return this.httpClient.post<ApiResponse<ProductSupplierQuote>>(
      `${this.basePath}/api/productSupplierQuotes`,
      payload,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders
      }
    );
  }

  /**
   * Update supplier quote information
   * PUT /api/productSupplierQuotes/{id}
   */
  updateSupplierQuote(
    id: string,
    payload: UpdateSupplierQuotePayload
  ): Observable<ApiResponse<ProductSupplierQuote>> {
    return this.httpClient.put<ApiResponse<ProductSupplierQuote>>(
      `${this.basePath}/api/productSupplierQuotes/${id}`,
      payload,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders
      }
    );
  }

  /**
   * Delete a supplier quote record
   * DELETE /api/productSupplierQuotes/{id}
   */
  deleteSupplierQuote(id: string): Observable<ApiResponse<any>> {
    return this.httpClient.delete<ApiResponse<any>>(
      `${this.basePath}/api/productSupplierQuotes/${id}`,
      {
        withCredentials: this.configuration.withCredentials,
        headers: this.defaultHeaders
      }
    );
  }
}
